import { useQuery } from '@tanstack/react-query'
import { AlertTriangleIcon, ChevronRightIcon, Loader2Icon } from 'lucide-react'
import { useState } from 'react'

import { specialContractsCustomerApi, type StatusDoContrato } from '@nebula/shared/services'
import { useAuthStore } from '@nebula/shared/store'
import { getErrorMessage } from '@nebula/shared/utils'

import {
  COR_DO_STATUS,
  ROTULO_DA_MODALIDADE,
  ROTULO_DO_STATUS,
  escopoDoContrato,
  vigenciaDoContrato,
} from './specialContractLabels'
import { chavesDosContratosEspeciais } from './specialContractQueryKeys'

const FILTROS: Array<{ valor: StatusDoContrato | 'all'; rotulo: string }> = [
  { valor: 'all', rotulo: 'Todos' },
  { valor: 'active', rotulo: 'Ativos' },
  { valor: 'cancelled', rotulo: 'Cancelados' },
  { valor: 'expired', rotulo: 'Expirados' },
]

/**
 * Contratos especiais do cliente, na aba Contratos (#1091 F3, item 26): todos os status, de
 * todas as revendas, cada um com a revenda, a modalidade, o escopo legível e a vigência. A
 * lista vem inteira e o filtro por status é da tela. Sem contrato nenhum, a seção não aparece.
 */
const SpecialContractsSection = ({ onAbrir }: { onAbrir: (id: string) => void }) => {
  const [filtro, setFiltro] = useState<StatusDoContrato | 'all'>('all')
  const orgId = useAuthStore((s) => s.activeOrganizationId)
  const { data, isLoading, error } = useQuery({
    queryKey: chavesDosContratosEspeciais.lista(orgId),
    queryFn: () => specialContractsCustomerApi.list(),
    enabled: orgId !== null,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6 text-gray-600">
        <Loader2Icon className="mr-2 size-5 animate-spin" />
        Carregando contratos especiais...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <AlertTriangleIcon className="size-5" />
        Erro ao carregar contratos especiais: {getErrorMessage(error)}
      </div>
    )
  }

  const contratos = data?.contracts ?? []
  if (contratos.length === 0) return null
  const visiveis = filtro === 'all' ? contratos : contratos.filter((c) => c.status === filtro)

  return (
    <section className="rounded-lg bg-white shadow" aria-labelledby="titulo-contratos-especiais">
      <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <h2 id="titulo-contratos-especiais" className="text-lg font-semibold text-gray-900">
          Contratos especiais
        </h2>
        <div className="flex flex-wrap gap-2">
          {FILTROS.map((f) => (
            <button
              key={f.valor}
              type="button"
              onClick={() => setFiltro(f.valor)}
              aria-pressed={filtro === f.valor}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                filtro === f.valor
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.rotulo}
            </button>
          ))}
        </div>
      </div>
      {visiveis.length === 0 ? (
        <p className="p-6 text-sm text-gray-500">Nenhum contrato com este status.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {visiveis.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-6"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900">{c.name}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${COR_DO_STATUS[c.status] ?? 'bg-gray-100 text-gray-700'}`}
                  >
                    {ROTULO_DO_STATUS[c.status] ?? c.status}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {ROTULO_DA_MODALIDADE[c.contract_type] ?? c.contract_type} ·{' '}
                  <span>{escopoDoContrato(c)}</span>
                </div>
                <div className="text-xs text-gray-500">
                  Revenda: {c.reseller_name ?? '—'} · {vigenciaDoContrato(c)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onAbrir(c.id)}
                aria-label={`Ver detalhes de ${c.name}`}
                className="inline-flex items-center gap-1 self-start text-sm font-medium text-purple-700 hover:underline sm:self-center"
              >
                Ver detalhes <ChevronRightIcon className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default SpecialContractsSection
